"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { connectGoogle, disconnectGoogle, loadStoredAccount, type ConnectedAccount } from "@/lib/emailCleaner/googleAuth";
import { fetchInboxProfile } from "@/lib/emailCleaner/gmail";
import type { InboxProfile } from "@/lib/emailCleaner/types";
import Dashboard from "./Dashboard";
import type { FeatureKey } from "./featureKeys";
import HomeTab, { type HomeView } from "./tabs/HomeTab";
import InboxTab from "./tabs/InboxTab";
import SmartCleanTab from "./tabs/SmartCleanTab";
import FoldersTab from "./tabs/FoldersTab";
import MoreTab, { type MoreView } from "./tabs/MoreTab";

const HOME_VIEWS: Partial<Record<FeatureKey, HomeView>> = {
  explore: { name: "explore" },
  "inbox-overview": { name: "inbox-overview" },
  unsubscribe: { name: "unsubscribe" },
  "block-mute": { name: "block-mute" },
  "stop-unwanted": { name: "stop-unwanted" },
  "auto-organize": { name: "auto-organize" },
  "daily-brief": { name: "daily-brief" },
  "recent-activity": { name: "recent-activity" },
};

const MORE_VIEWS: Partial<Record<FeatureKey, MoreView>> = {
  "ai-assistant": { name: "ai-assistant" },
  "ai-summary": { name: "ai-summary" },
  "ai-insights": { name: "ai-insights" },
  "ai-cleanup-coach": { name: "ai-cleanup-coach" },
  senders: { name: "senders" },
  subscriptions: { name: "subscriptions" },
  attachments: { name: "attachments" },
  "automation-rules": { name: "automation-rules" },
  analytics: { name: "analytics" },
  "storage-analyzer": { name: "storage-analyzer" },
  productivity: { name: "productivity" },
};

function renderFeature(feature: FeatureKey, accessToken: string, accountEmail: string, onExit: () => void) {
  if (feature === "inbox") return <InboxTab accessToken={accessToken} accountEmail={accountEmail} onExit={onExit} />;
  if (feature === "smart-clean") return <SmartCleanTab accessToken={accessToken} accountEmail={accountEmail} onExit={onExit} />;
  if (feature === "folders") return <FoldersTab accessToken={accessToken} accountEmail={accountEmail} onExit={onExit} />;

  const homeView = HOME_VIEWS[feature];
  if (homeView) return <HomeTab accessToken={accessToken} accountEmail={accountEmail} initialView={homeView} onExit={onExit} />;

  const moreView = MORE_VIEWS[feature];
  if (moreView) return <MoreTab accessToken={accessToken} accountEmail={accountEmail} initialView={moreView} onExit={onExit} />;

  return null;
}

export default function EmailCleanerApp() {
  const [account, setAccount] = useState<ConnectedAccount | null>(null);
  const [ready, setReady] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [profile, setProfile] = useState<InboxProfile | null>(null);
  const [feature, setFeature] = useState<FeatureKey | null>(null);

  useEffect(() => {
    // Hydrating from sessionStorage (an external store) must happen after mount to
    // avoid a server/client markup mismatch, so this legitimately sets state on mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAccount(loadStoredAccount());
    setReady(true);
  }, []);

  useEffect(() => {
    if (account) {
      fetchInboxProfile(account.accessToken).then(setProfile).catch(() => {});
    }
  }, [account]);

  async function handleConnect() {
    setConnecting(true);
    setConnectError(null);
    try {
      const connected = await connectGoogle();
      setAccount(connected);
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : "Couldn't connect your Gmail account.");
    } finally {
      setConnecting(false);
    }
  }

  function handleDisconnect() {
    disconnectGoogle(account?.accessToken ?? null);
    setAccount(null);
    setProfile(null);
    setFeature(null);
  }

  if (!ready) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-[#0f172a]/80 dark:text-slate-400">
        Loading…
      </div>
    );
  }

  if (!account) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-8 text-center backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
        <Image src="/email-cleaner/logo.png" alt="Maila logo" width={72} height={72} className="mx-auto rounded-2xl" />
        <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">Connect your inbox</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">
          Sign in with Google to let Maila read and help clean your Gmail inbox, right here in your browser.
          Nothing is deleted without your say — every cleanup action runs through the same AI backend as the
          Maila app.
        </p>
        {connectError && (
          <p className="mx-auto mt-4 max-w-md rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            {connectError}
            {connectError.toLowerCase().includes("blocked") || connectError.toLowerCase().includes("access_denied") ? (
              <> If this app is still in Google&apos;s testing mode, your account needs to be added as a test user first.</>
            ) : null}
          </p>
        )}
        <button
          type="button"
          onClick={handleConnect}
          disabled={connecting}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {connecting ? "Connecting…" : "Connect your Gmail"}
        </button>
        <p className="mx-auto mt-4 max-w-md text-xs text-slate-400 dark:text-slate-500">
          Requests read + modify access to Gmail so cleanup actions (archive, trash) can actually run.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-r from-white via-violet-50/60 to-white p-4 backdrop-blur-sm dark:border-slate-800 dark:from-[#0f172a] dark:via-violet-500/5 dark:to-[#0f172a]">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {account.picture ? (
              <Image src={account.picture} alt={account.name} width={48} height={48} className="rounded-full ring-2 ring-white shadow-md dark:ring-slate-800" unoptimized />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg font-semibold text-white shadow-md">
                {account.email[0]?.toUpperCase() ?? "?"}
              </div>
            )}
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">{account.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{account.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {profile && (
              <span className="rounded-full border border-violet-200/80 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 dark:border-violet-800/60 dark:bg-slate-900 dark:text-violet-400">
                {profile.messagesTotal.toLocaleString()} messages in account
              </span>
            )}
            <button
              type="button"
              onClick={handleDisconnect}
              className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-red-300 hover:text-red-600 dark:border-slate-700 dark:text-slate-300"
            >
              Disconnect
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {feature ? (
          renderFeature(feature, account.accessToken, account.email, () => setFeature(null))
        ) : (
          <Dashboard accessToken={account.accessToken} accountEmail={account.email} onOpen={setFeature} />
        )}
      </div>
    </div>
  );
}
